import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, RefreshCw, ExternalLink, Loader2 } from "lucide-react";
import {
  BatchStatus,
  BatchStatusCoverSheetLine,
  BatchStatusTicket,
  getBatchStatus,
  getTicketPdfUrl,
} from "../services/publicBatchApiService";

// Public, read-only view of one batch for operations managers. Linked from the submission
// email and the scans page sidebar. Deliberately does not touch localStorage.

const REFRESH_MS = 60_000;

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTime = (utc: string) => {
  const d = new Date(utc.endsWith("Z") ? utc : `${utc}Z`);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const formatQty = (qty: number | null, uom: string | null) =>
  qty == null ? "—" : `${Number(qty.toFixed(2))}${uom ? ` ${uom}` : ""}`;

const ticketStatus = (status: string | null) => {
  switch (status) {
    case "Pushed":
      return { label: "Processed", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" };
    case "Deleted":
      return { label: "Removed", cls: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300" };
    default:
      return { label: "In review", cls: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300" };
  }
};

const BatchStatusPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const [data, setData] = useState<BatchStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scenario, setScenario] = useState<0 | 1>(0);

  const load = useCallback(
    async (initial = false) => {
      if (!batchId) return;
      if (!initial) setRefreshing(true);
      try {
        setData(await getBatchStatus(batchId));
        setError(null);
      } catch (e: any) {
        setError(e?.response?.status === 404 ? "Batch not found." : "Could not load this batch. Try again shortly.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [batchId],
  );

  useEffect(() => {
    load(true);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-background-dark text-slate-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading batch...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-background-dark p-4">
        <div className="text-center">
          <p className="text-lg font-bold text-slate-800 dark:text-slate-100">{error ?? "Batch not found."}</p>
          <p className="text-sm text-slate-500 mt-1 font-mono">{batchId}</p>
        </div>
      </div>
    );
  }

  const activeTickets = data.tickets.filter((t) => t.status !== "Deleted");
  const processed = activeTickets.filter((t) => t.status === "Pushed").length;
  const submittedLoads = data.coverSheetSubmitted.reduce((acc, l) => acc + l.loads, 0);
  const isComplete = data.status === "Completed" || data.status === "Pushed";
  const isDeleted = data.status === "Deleted";
  const hasAccounting = data.coverSheetAccounting.length > 0;
  const coverSheet = scenario === 0 || !hasAccounting ? data.coverSheetSubmitted : data.coverSheetAccounting;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background-dark text-slate-900 dark:text-white font-display">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Header */}
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FileText size={20} className="text-[#107c41] shrink-0" />
              <h1 className="text-xl font-extrabold truncate">
                {data.branch} {data.warehouse}
              </h1>
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${
                  isDeleted
                    ? "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300"
                    : isComplete
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300"
                }`}
              >
                {isDeleted ? "Deleted" : isComplete ? "Complete" : "In accounting"}
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Tickets for <span className="font-semibold">{formatDate(data.orderDate)}</span>
              {data.submittedAtUtc && <> · submitted {formatDateTime(data.submittedAtUtc)}</>}
            </p>
            <p className="text-xs font-mono text-slate-400 mt-0.5">{data.batchId}</p>
          </div>
          <button
            onClick={() => load()}
            disabled={refreshing}
            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 hover:text-[#107c41] shrink-0"
            title="Refresh"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>
        </header>

        {isDeleted && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-500/20 p-4 text-sm text-rose-900 dark:text-rose-200">
            This batch was deleted. Check the plant's other submissions for this date.
          </div>
        )}

        {/* Summary */}
        {data.isNoMaterial ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 p-4 text-amber-900 dark:text-amber-200 font-semibold">
            The plant reported no tickets for this day.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Loads on cover sheet" value={submittedLoads} />
            <Stat label="Tickets received" value={activeTickets.length + data.processingCount} />
            <Stat label="Processed by accounting" value={`${processed} / ${activeTickets.length}`} />
          </div>
        )}

        {data.processingCount > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-800 dark:text-sky-200 text-sm px-3 py-2">
            <Loader2 size={14} className="animate-spin shrink-0" />
            {data.processingCount} scanned page{data.processingCount === 1 ? " is" : "s are"} still being read.
          </div>
        )}

        {data.plantNotes
          .filter((n) => n.note)
          .map((n, i) => (
            <div key={i} className="rounded-lg border-l-4 border-[#107c41] bg-white dark:bg-slate-900 px-3 py-2 text-sm">
              <span className="font-semibold">Plant note: </span>
              {n.note}
            </div>
          ))}

        {/* Cover sheet */}
        {!data.isNoMaterial && (
          <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <h2 className="font-bold">Cover sheet</h2>
              <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 text-xs font-semibold">
                <ToggleButton active={scenario === 0} onClick={() => setScenario(0)}>
                  Plant submitted
                </ToggleButton>
                <ToggleButton
                  active={scenario === 1}
                  disabled={!hasAccounting}
                  onClick={() => setScenario(1)}
                >
                  Accounting
                </ToggleButton>
              </div>
            </div>
            <CoverSheetTable lines={coverSheet} />
          </section>
        )}

        {/* Tickets */}
        {data.tickets.length > 0 && (
          <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <h2 className="font-bold">Tickets</h2>
            </div>
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.tickets.map((t) => (
                <TicketRow key={t.id} batchId={data.batchId} ticket={t} />
              ))}
            </ul>
          </section>
        )}

        <p className="text-center text-xs text-slate-400 pt-2">Updates automatically every minute.</p>
      </div>
    </div>
  );
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
    <div className="text-2xl font-extrabold">{value}</div>
    <div className="text-[11px] leading-tight text-slate-500 dark:text-slate-400 mt-1">{label}</div>
  </div>
);

const ToggleButton = ({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={disabled ? "Accounting hasn't adjusted this cover sheet yet" : undefined}
    className={`px-2.5 py-1 rounded-md transition-colors disabled:opacity-40 ${
      active ? "bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white" : "text-slate-500"
    }`}
  >
    {children}
  </button>
);

const CoverSheetTable = ({ lines }: { lines: BatchStatusCoverSheetLine[] }) => {
  if (lines.length === 0) return <p className="px-4 py-3 text-sm text-slate-400 italic">No lines.</p>;
  const total = lines.reduce((acc, l) => acc + l.loads, 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50">
            <th className="px-4 py-2 font-semibold">Material</th>
            <th className="px-2 py-2 font-semibold">Supplier</th>
            <th className="px-2 py-2 font-semibold">Hauler</th>
            <th className="px-2 py-2 font-semibold text-right">Loads</th>
            <th className="px-4 py-2 font-semibold text-right">Qty</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="px-4 py-2 font-medium">{l.inventoryDesc ?? l.inventoryId}</td>
              <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{l.vendorName ?? l.vendorId}</td>
              <td className="px-2 py-2 text-slate-600 dark:text-slate-300">{l.freightVendorName ?? l.freightVendorId}</td>
              <td className="px-2 py-2 text-right tabular-nums">{l.loads}</td>
              <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{formatQty(l.receivedQty, l.uom)}</td>
            </tr>
          ))}
          <tr className="font-bold">
            <td className="px-4 py-2" colSpan={3}>
              Total
            </td>
            <td className="px-2 py-2 text-right tabular-nums">{total}</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const TicketRow = ({ batchId, ticket }: { batchId: string; ticket: BatchStatusTicket }) => {
  const s = ticketStatus(ticket.status);
  const removed = ticket.status === "Deleted";
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 ${removed ? "opacity-60" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className={`font-semibold font-mono text-sm ${removed ? "line-through" : ""}`}>
          {ticket.ticketNumber || "No ticket #"}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {ticket.inventoryDesc ?? ticket.inventoryId ?? "Unknown material"} · {formatQty(ticket.receivedQty, ticket.uom)}
        </div>
      </div>
      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${s.cls}`}>{s.label}</span>
      <a
        href={getTicketPdfUrl(batchId, ticket.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 text-xs font-semibold text-[#107c41] hover:underline shrink-0"
      >
        PDF <ExternalLink size={12} />
      </a>
    </li>
  );
};

export default BatchStatusPage;
